import type {
  WorkflowConditionConditionType,
  WorkflowExpressionCondition,
  WorkflowExpressionGroup,
} from '@/api/entity_types/workflow/WorkflowCondition';
import type {
  WorkflowConditionOperator,
  WorkflowConditionProperty,
  WorkflowDefinitionConditionEntity,
} from '@/api/entity_types/workflow/WorkflowDefinition';
import { uuid } from '@/utilities/uuid';
import { computed, ref, toRaw } from 'vue';

export type UseWorkflowConditionEditorArgs = {
  type: WorkflowConditionConditionType;
  getDefinitionList: () => WorkflowDefinitionConditionEntity[];
  getCurrentExpression: () => WorkflowExpressionGroup | undefined;
};

export type UseWorkflowConditionEditorReturn = ReturnType<typeof useWorkflowConditionEditor>;

export const useWorkflowConditionEditor = ({
  type,
  getDefinitionList,
  getCurrentExpression,
}: UseWorkflowConditionEditorArgs) => {
  const definitions = ref<WorkflowDefinitionConditionEntity[]>(getDefinitionList() || []);

  const newRoot = () => {
    const group = makeGroup();
    const condition = makeDefaultCondition(definitions.value[0]);
    group.items.push(condition);
    return group;
  };

  const initPersistedExpression = () => {
    const expression = getCurrentExpression();
    return expression ? deepClone(expression) : null;
  };

  const initLocalExpression = () => {
    const expression = getCurrentExpression();
    return expression ? deepClone(expression) : newRoot();
  };

  const persistedExpression = ref<WorkflowExpressionGroup | null>(initPersistedExpression());
  const localExpression = ref<WorkflowExpressionGroup>(initLocalExpression());

  const isConditionNew = computed(() => !persistedExpression.value);
  const hasStagedChanges = computed(
    () => JSON.stringify(persistedExpression.value) !== JSON.stringify(localExpression.value)
  );

  const syncPersistedExpression = () => {
    persistedExpression.value = deepClone(localExpression.value);
  };

  const addCondition = () => {
    const condition = makeDefaultCondition(definitions.value[0]);
    localExpression.value.items.push(condition);
  };

  const discardChanges = () => {
    localExpression.value = persistedExpression.value ? deepClone(persistedExpression.value) : newRoot();
  };

  type ChangeConditionArgs = {
    index: number;
    property?: WorkflowConditionProperty;
    operator?: WorkflowConditionOperator;
    value?: { newVal?: string };
  };
  const changeCondition = ({ index, property, operator, value }: ChangeConditionArgs) => {
    if (!localExpression.value) throw errorMessage('local expression');
    if (localExpression.value.items[index].type !== 'Condition') return;

    if (property) localExpression.value.items[index].property = property;
    if (operator) localExpression.value.items[index].operator = operator;
    if (value) localExpression.value.items[index].value = value.newVal;
  };

  const removeCondition = ({ index }: { index: number }) => {
    if (!localExpression.value) throw errorMessage('local expression');
    localExpression.value.items.splice(index, 1);
  };

  const resetToNew = () => {
    localExpression.value = makeGroup();
    persistedExpression.value = null;
  };

  return {
    type,
    localExpression,
    isConditionNew,
    hasStagedChanges,
    definitions,
    addCondition,
    changeCondition,
    removeCondition,
    discardChanges,
    syncPersistedExpression,
    resetToNew,
  };
};

const makeGroup = (): WorkflowExpressionGroup => ({
  id: uuid.v4(),
  type: 'Group',
  operator: 'AND',
  items: [],
});

const makeDefaultCondition = (firstFilter: WorkflowDefinitionConditionEntity): WorkflowExpressionCondition => {
  const condition: WorkflowExpressionCondition = {
    id: uuid.v4(),
    type: 'Condition',
    property: firstFilter.property,
    operator: firstFilter.operators[0].type,
    value: firstFilter.operators[0].value?.options[0],
  };
  return condition;
};

const deepClone = <T>(value: T): T => {
  return JSON.parse(JSON.stringify(toRaw(value))) as T;
};

const errorMessage = (property: string) => new Error(`Workflow condition's ${property} is missing`);
